// @ts-nocheck
import React, { useContext } from 'react'

import QRCode from 'qrcode.react'
import { BrowserView, MobileView, isDesktop, isIOS, isAndroid } from 'react-device-detect'

import Alert from '@react/react-spectrum/Alert'
import Heading from '@react/react-spectrum/Heading'
import { Tab, TabView } from '@react/react-spectrum/TabView'
import Wait from '@react/react-spectrum/Wait'
import Well from '@react/react-spectrum/Well'

import { RootContext } from 'root'
import { SUPER_USERS } from 'config/constants'
import { useAppVersions, useDevAppVersions } from './hooks'

import './Install.scss'

export default function Install() {
  const { data, loading, error } = useAppVersions()
  const { user } = useContext(RootContext)
  const isSuperUser = SUPER_USERS.includes(user.id)

  return (
    <div className='install-view'>
      <div className='wrapper'>
        <section>
          <Heading variant='pageTitle'>Live Demos App</Heading>
          <p>
            Live Demos App lets you easily demonstrate Adobe Experience Cloud from mobile perspective.{' '}
            <a
              href='https://dsn.adobe.com/docs/live-demos-mobile-app'
              className='spectrum-Link'
              target='_blank'
              rel='noopener noreferrer'
            >
              Learn more
            </a>
          </p>
        </section>

        <section>
          {error && (
            <div>
              <Alert header='Error when loading app versions' variant='error'>
                {error.message}
              </Alert>
            </div>
          )}

          {loading && <Wait />}

          {data && !error && (
            <TabView aria-label='Installers'>
              <Tab label='Live Demos App'>
                <div className='installer-panels'>
                  <div className='alpha-warning'>
                    <p>
                      <strong>Live Demos is the new app that replaces the DXDemo mobile app.</strong>
                      <p>
                        It supports latest features and enhancements and is designed to provide a more modern, scalable,
                        and reliable experience.
                        <br /> Live Demos is compatible with projects created on or after August 1st.
                      </p>
                    </p>
                  </div>
                  {(isDesktop || isIOS) && (
                    <InstallerPanel
                      platform='iOS'
                      version={data?.v1?.iOS}
                      url='itms-services://?action=download-manifest&url=https://demo-system-next.s3.amazonaws.com/dsn-mobile-app/Helium3/web-installer/assets/helium3.plist'
                    />
                  )}

                  {(isDesktop || isAndroid) && (
                    <InstallerPanel
                      platform='Android'
                      version={data?.v1?.android}
                      url='https://demo-system-next.s3.amazonaws.com/dsn-mobile-app/Helium3/builds/release/android/helium3-latest.apk'
                    />
                  )}
                </div>
              </Tab>
              <Tab label='DX Demo App'>
                <div className='installer-panels'>
                  <div className='alpha-warning'>
                    <p>
                      <strong>DXDemo app has been replaced by the Live Demos app.</strong>
                      <p>
                        While DXDemo is no longer actively supported and won’t receive new features or bug fixes, your
                        existing projects will continue to work as expected. The app is not being decommissioned
                        immediately.
                        <br />
                        <strong>Going forward,</strong> Quick Setup will create projects tailored for the Live Demos
                        app.
                      </p>
                    </p>
                  </div>
                  {(isDesktop || isIOS) && (
                    <InstallerPanel
                      platform='iOS'
                      version={data?.v2?.iOS}
                      url='itms-services://?action=download-manifest&url=https://demo-system-next.s3.amazonaws.com/dxdemo/web-installer/assets/dxdemo.plist'
                    />
                  )}

                  {(isDesktop || isAndroid) && (
                    <InstallerPanel
                      platform='Android'
                      version={data?.v2?.android}
                      url='https://demo-system-next.s3.amazonaws.com/dxdemo/builds/release/android/dxdemo-latest.apk'
                    />
                  )}
                </div>
              </Tab>

              {isSuperUser && (
                <Tab label='Dev Version'>
                  <DevInstallerTab />
                </Tab>
              )}
            </TabView>
          )}
        </section>

        <section>
          <Heading variant='subtitle1'>Troubleshooting</Heading>
          <p>
            If you run into any issues when installing or running the app, please refer to{' '}
            <a
              href='https://dsn.adobe.com/docs/live-demos-mobile-app'
              className='spectrum-Link'
              target='_blank'
              rel='noopener noreferrer'
            >
              this documentation page
            </a>
          </p>
        </section>
      </div>
    </div>
  )
}

function DevInstallerTab() {
  const { data, loading, error } = useDevAppVersions()

  if (loading) return <Wait />
  if (error)
    return (
      <Alert header='Error when loading dev app versions' variant='error'>
        {error.message}
      </Alert>
    )

  return (
    <div className='installer-panels'>
      {(isDesktop || isIOS) && (
        <InstallerPanel
          platform='iOS'
          version={data?.iOS}
          url='itms-services://?action=download-manifest&url=https://demo-system-next.s3.amazonaws.com/dsn-mobile-app/Helium3/web-installer-dev/assets/helium3.plist'
        />
      )}

      {(isDesktop || isAndroid) && (
        <InstallerPanel
          platform='Android'
          version={data?.android}
          url='https://demo-system-next.s3.amazonaws.com/dsn-mobile-app/Helium3/builds/development/android/helium3-latest.apk'
        />
      )}
    </div>
  )
}

function InstallerPanel({ platform, version, url }) {
  return (
    <div className='installer-panel'>
      <Well>
        <BrowserView className='installer-content'>
          <Heading variant='subtitle2'>
            {platform} - {version}
          </Heading>

          <QRCode value={url || 'http://facebook.github.io/react/'} />

          <p>
            <em>Scan this QR code on your mobile device</em>
          </p>
        </BrowserView>

        <MobileView className='installer-content'>
          <Heading variant='subtitle2'>
            {platform} - {version}
          </Heading>

          <a href={url}>
            <button class='spectrum-Button spectrum-Button--cta'>
              <span class='spectrum-Button-label'>Install on this device</span>
            </button>
          </a>
        </MobileView>
      </Well>
    </div>
  )
}
